/**
 * sab-protocol.ts — SharedArrayBuffer wire-contract between the player-worker
 * (which runs libavformat's AVIO read-callback = the demuxer) and a dedicated
 * fetch-worker (which does the async byte-range fetches). Plan reference:
 * ~/.claude/plans/webplayer-snappy.md §"A-P1.0 SAB-AVIO-Protokoll".
 *
 * Why this exists: libavformat's read-callback must return bytes SYNCHRONOUSLY,
 * but in the browser bytes arrive via fetch (async). We bridge sync-over-async
 * with a SharedArrayBuffer + Atomics: the demuxer's read-callback publishes a
 * request and blocks on Atomics.wait; a SEPARATE worker (never the demuxer's
 * own — a blocking wait there would starve its event loop and the fetch would
 * never resolve) fulfils it via fetch and wakes it via Atomics.notify. No
 * Asyncify, and no pthread / -sSHARED_MEMORY WASM build — the demuxer copies
 * plain bytes out of the shared window into its own (non-shared) heap.
 *
 * Kept dependency-free so it compiles into both the main-thread bundle and the
 * classic (IIFE) fetch-worker bundle.
 */

/**
 * Control-SAB: an Int32Array with a fixed 16-slot layout. Slots past ABORT are
 * reserved so the header can grow additively without a wire-break.
 *
 * 64-bit byte offsets are split lo/hi across two slots — reconstruct with
 * joinOff(lo, hi). `lo` holds the low 32 bits (unsigned bit-pattern, stored as
 * a signed int32 by Int32Array coercion); `hi` holds floor(off / 2**32).
 */
export const Ctrl = {
  /** SabState — the single synchronization word both sides wait/notify on. */
  STATE:       0,
  /** Requested byte offset, low 32 bits. */
  REQ_OFF_LO:  1,
  /** Requested byte offset, high 32 bits. */
  REQ_OFF_HI:  2,
  /** Requested length (informational — the fetcher always reads a full window
   *  so the demuxer gets readahead cache-hits). */
  REQ_LEN:     3,
  /** Bytes actually placed into the data-SAB (0 = EOF). */
  RESP_LEN:    4,
  /** Byte offset the response window starts at, low 32 bits. */
  RESP_OFF_LO: 5,
  /** Byte offset the response window starts at, high 32 bits. */
  RESP_OFF_HI: 6,
  /** Non-zero → cancel the in-flight read (seek-cancel / teardown). Reset to 0
   *  before the next read. */
  ABORT:       7,
  /** HTTP status of the last STATE=ERROR publish (0 otherwise). The fetch-worker
   *  writes it before flipping STATE=ERROR so the demuxer-side (which only sees
   *  a generic read failure) can route recovery — a 404 means the server-side
   *  /stream session was reaped → directSessionLost instead of a fatal error. */
  ERR_CODE:    8,
  /**
   * Read-ahead high-water-mark, low/high 32 bits: the byte offset up to which
   * the fetch-worker holds contiguous bytes it can serve WITHOUT a new network
   * fetch (current data-window end, extended by the proactively-filled local
   * read-ahead buffer). The player-worker's pull-loop peeks this (against the
   * demuxer's current read position) BEFORE readPacket, so it can yield instead
   * of entering the blocking SAB read when the next bytes aren't local yet. Only
   * a heuristic gate — a torn lo/hi read at most causes one extra yield/retry.
   * 0 = uninitialised (nothing filled yet) → the pull-loop does not gate.
   */
  AVAIL_OFF_LO: 9,
  AVAIL_OFF_HI: 10,
} as const;

/** Number of Int32 slots in the control-SAB. */
export const CTRL_SLOTS = 16;

/** STATE values (Ctrl.STATE slot). */
export const State = {
  IDLE:      0,
  REQUESTED: 1,
  FETCHING:  2,
  READY:     3,
  ERROR:     4,
} as const;

/** Data-SAB window size — one readahead window (4 MiB per the contract). */
export const DATA_WINDOW_BYTES = 4 * 1024 * 1024;

// ── 64-bit offset split/join (offsets are always non-negative) ──────────

/** Low 32 bits of a byte offset (unsigned). Int32Array coerces to signed on store. */
export function offLo(off: number): number { return off >>> 0; }

/** High 32 bits of a byte offset. */
export function offHi(off: number): number { return Math.floor(off / 2 ** 32); }

/** Rebuild a 64-bit-ish byte offset from its lo/hi halves (as read from the SAB). */
export function joinOff(lo: number, hi: number): number { return (lo >>> 0) + hi * 2 ** 32; }

// ── Fetch-worker command protocol (main-thread → fetch-worker) ──────────

/** One-time init: hands over both SABs + the resolved stream URL. */
export interface FetchWorkerInit {
  type:        'init';
  ctrlSab:     SharedArrayBuffer;
  dataSab:     SharedArrayBuffer;
  /** Fully-resolved, auth-ready URL (token already appended by the caller). */
  url:         string;
  /** fetch credentials mode (cookie auth on same-origin /stream/<sid>). */
  credentials: RequestCredentials;
  /** Total file size (from streamOpen Content-Length) — bounds readahead + EOF. */
  fileSize:    number;
}

export type FetchWorkerCmd =
  | FetchWorkerInit
  /** Abort the in-flight fetch (seek-cancel). The loop keeps running. */
  | { type: 'abort' }
  /** Abort + self.close() the worker. */
  | { type: 'teardown' };

// ── Shape the demuxer publishes into the worker-scope global ────────────

/** What `globalThis.__zapdSab` carries for the WASM EM_JS read/seek callbacks. */
export interface ZapdSabGlobal {
  /** Int32Array view over the control-SAB. */
  ctrl:     Int32Array;
  /** Uint8Array view over the data-SAB window. */
  data:     Uint8Array;
  /** Total file size (for AVSEEK_SIZE). */
  fileSize: number;
}
